import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  ArrowRight,
  Clock3,
  LayoutTemplate,
  Loader2,
  Sparkles,
  Wand2,
  Wrench,
} from "lucide-react";
import { listPageTemplates } from "@ease/page-builder";
import { buildPickOneCustomizationPath } from "@/hooks/use-vertical-templates";
import { duplicatePage, isPersonalTemplatePage, usePageList } from "@/hooks/use-pages";
import { useTemplateRegistry } from "@/hooks/use-template-registry";
import { TemplateBrowser } from "@/components/pages/template-browser";
import { ApiError } from "@/lib/api-client";

const START_PATHS = [
  {
    id: "tell-me" as const,
    icon: Sparkles,
    title: "Tell Me",
    duration: "~2 minutes",
    description: "Describe the business, offer, and goal. AI turns that into a real draft checkpoint you can review before opening the editor.",
    bestFor: "You want the fastest guided start",
    href: "/pages/ai-generate",
    gradient: "var(--gradient-ai)",
    recommended: true,
  },
  {
    id: "pick-one" as const,
    icon: LayoutTemplate,
    title: "Pick One",
    duration: "~5 minutes",
    description: "Start from a real curated vertical starter and turn it into an AI-backed draft instead of beginning from a blank canvas.",
    bestFor: "You know the style or industry you want",
    href: null,
    gradient: "var(--gradient-energy)",
    recommended: false,
  },
  {
    id: "build-it" as const,
    icon: Wrench,
    title: "Build It",
    duration: "10+ minutes",
    description: "Open the full editor on a blank page and assemble the layout manually with sections, SEO tools, and publishing controls.",
    bestFor: "You already know exactly what to build",
    href: "/pages/editor/new",
    gradient: "var(--gradient-brand)",
    recommended: false,
  },
] as const;

type PathId = (typeof START_PATHS)[number]["id"];

const STORAGE_KEY = "ease_page_builder_path";

export default function NewPageEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hoveredPath, setHoveredPath] = useState<PathId | null>(null);
  const [lastChoice, setLastChoice] = useState<PathId | null>(null);
  const [reuseError, setReuseError] = useState<string | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const { templates: registryTemplates, isLoading: templatesLoading, error: templateError } = useTemplateRegistry();
  const fallbackTemplates = useMemo(() => listPageTemplates(), []);
  const starterTemplates = registryTemplates.length > 0 ? registryTemplates : fallbackTemplates;
  const starterGalleryRef = useRef<HTMLElement | null>(null);
  const siteId = searchParams.get("siteId");
  const source = searchParams.get("source");
  const legacySource = searchParams.get("legacy");
  const { pages } = usePageList();
  const personalTemplates = useMemo(
    () => pages.filter((page) => isPersonalTemplatePage(page)),
    [pages],
  );
  const isFirstRun = source === "onboarding";

  useEffect(() => {
    setLastChoice(readStoredChoice());
  }, []);

  useEffect(() => {
    const requestedMode = searchParams.get("mode");
    if (!requestedMode) {
      return;
    }

    const selectedPath = START_PATHS.find((path) => path.id === requestedMode);
    if (!selectedPath) {
      return;
    }

    requestAnimationFrame(() => {
      handleSelect(selectedPath.id);
    });
    // The search param is only used as an entry accelerator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const currentRecommendation = useMemo(() => {
    return START_PATHS.find((path) => path.recommended) ?? START_PATHS[0];
  }, []);

  const handleReapplyPersonalTemplate = async (pageId: string, title: string) => {
    setActiveTemplateId(pageId);
    setReuseError(null);

    try {
      const duplicated = await duplicatePage({
        pageId,
        title: title.replace(/\s+Template$/i, ""),
        mode: "template_apply",
      });

      navigate(`/pages/editor/${duplicated.id}?entry=template`);
    } catch (error) {
      setReuseError(error instanceof ApiError ? error.message : "Failed to reapply the selected personal template.");
    } finally {
      setActiveTemplateId(null);
    }
  };

  const handleSelect = (path: PathId) => {
    localStorage.setItem(STORAGE_KEY, path);
    setLastChoice(path);

    if (path === "pick-one") {
      starterGalleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const selectedPath = START_PATHS.find((item) => item.id === path);
    if (!selectedPath?.href) {
      return;
    }

    const target = new URL(selectedPath.href, window.location.origin);
    if (siteId) {
      target.searchParams.set("siteId", siteId);
    }
    if (source) {
      target.searchParams.set("source", source);
    }

    navigate(`${target.pathname}${target.search}`);
  };

  return (
    <div className="space-y-8">
      <section className="rounded-[28px] border border-border bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.04),rgba(15,23,42,0))] p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700 dark:text-emerald-300">
              <Wand2 className="h-3.5 w-3.5" />
              {isFirstRun ? "First-Run Path" : "Builder Entry"}
            </span>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              {isFirstRun ? "Choose the clearest first step." : "Choose the calmest way to start."}
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {isFirstRun
                ? "This is the truthful first-run decision point. Pick the outcome-first path that fits how much guidance you want, then keep moving without bouncing between old onboarding flows."
                : "The page builder no longer throws every user straight into the full editor. Start with a guided brief, an industry template, or the raw builder depending on how much help you want."}
            </p>
            {isFirstRun ? (
              <div className="rounded-[24px] border border-border bg-background/85 p-4 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">Recommended first move</p>
                    <p className="text-base font-semibold text-foreground">Start with Tell Me and review a real draft before you touch the full editor.</p>
                    <p className="text-sm leading-6 text-muted-foreground">This keeps the journey guided: brief, draft checkpoint, then editor continuation.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelect("tell-me")}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
                  >
                    Start with Tell Me
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : null}
            {legacySource === "website-import" ? (
              <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                Legacy website import onboarding was folded into this entry. Start with Tell Me if you want the most guided first draft.
              </p>
            ) : null}
          </div>

          <div className="rounded-[24px] border border-border bg-background/80 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {isFirstRun ? "Default first move" : "Best default"}
            </p>
            <p className="mt-2 text-base font-semibold text-foreground">{currentRecommendation.title}</p>
            <p className="mt-1 max-w-xs text-sm leading-6 text-muted-foreground">
              {isFirstRun
                ? "Start here if you want the clearest path to a real first draft. You can still move into templates or the editor after the draft checkpoint."
                : "Start here if you are not sure. You can still move into templates or the editor after the first draft."}
            </p>
          </div>
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-3">
        {START_PATHS.map((path) => {
          const Icon = path.icon;
          const isHovered = hoveredPath === path.id;
          const isLastChoice = lastChoice === path.id;

          return (
            <button
              key={path.id}
              type="button"
              onClick={() => handleSelect(path.id)}
              onMouseEnter={() => setHoveredPath(path.id)}
              onMouseLeave={() => setHoveredPath(null)}
              className={`relative flex h-full flex-col rounded-[26px] border p-6 text-left transition-all duration-200 ${
                path.recommended
                  ? "border-primary/30 bg-card shadow-lg"
                  : "border-border bg-card shadow-sm hover:border-primary/25"
              } ${isHovered ? "-translate-y-1 shadow-xl" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div
                  className="flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-sm"
                  style={{ background: path.gradient }}
                >
                  <Icon className="h-7 w-7" />
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                  {path.recommended ? (
                    <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">
                      Recommended
                    </span>
                  ) : null}
                  {isLastChoice ? (
                    <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                      Last used
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
                  <Clock3 className="h-3.5 w-3.5" />
                  {path.duration}
                </div>
                <h2 className="text-2xl font-semibold text-foreground">{path.title}</h2>
                <p className="text-sm leading-6 text-muted-foreground">{path.description}</p>
              </div>

              <div className="mt-6 rounded-2xl border border-border bg-background/70 px-4 py-3 text-sm text-foreground">
                <span className="font-medium">Best for:</span> {path.bestFor}
              </div>

              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary">
                Continue
                <ArrowRight className="h-4 w-4" />
              </div>
            </button>
          );
        })}
      </section>

      <section className="grid gap-4 lg:grid-cols-[1.05fr_0.95fr]">
        <article className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">
            {isFirstRun ? "How first run works" : "What changed"}
          </h2>
          <ul className="mt-4 space-y-3 text-sm leading-6 text-muted-foreground">
            <li>Tell Me creates a real checkpoint you review before anything reaches the editor.</li>
            <li>Pick One starts from a curated direction, then uses the same draft-review flow instead of a separate onboarding branch.</li>
            <li>Build It still opens the raw editor immediately for experienced users.</li>
            <li>Personal templates still reuse the same duplicate-page foundation, but stay private unless you later publish them to the marketplace.</li>
            <li>Community templates now live on the same mounted marketplace route family as creator submissions and admin moderation.</li>
            {isFirstRun ? <li>If you are unsure, use Tell Me first. You can still switch to templates or the full editor before generating anything.</li> : null}
          </ul>
        </article>

        <article className="rounded-[24px] border border-border bg-card p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-foreground">Need site setup first?</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Use the site manager to create a site container, choose a home page, and set global theme tokens before you polish individual pages.
          </p>
          <Link
            to="/pages/sites"
            className="mt-5 inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
          >
            Open site manager
            <ArrowRight className="h-4 w-4" />
          </Link>
        </article>
      </section>

      <section className="rounded-[28px] border border-border bg-card p-8 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              <LayoutTemplate className="h-3.5 w-3.5" />
              Personal Templates
            </span>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">Reapply your private templates without going through the marketplace.</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Saved personal templates are just private reusable page copies inside your tenant. Reapplying one creates a fresh draft page on the same canonical reuse path used by duplicate page.
            </p>
          </div>
          <Link
            to="/pages/sites?tab=pages"
            className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
          >
            Manage saved templates
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {reuseError ? (
          <div className="mt-5 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {reuseError}
          </div>
        ) : null}

        {personalTemplates.length === 0 ? (
          <div className="mt-6 rounded-[22px] border border-dashed border-border bg-background px-6 py-8 text-sm leading-6 text-muted-foreground">
            No personal templates yet. Save an existing page as a template from the site workspace, then come back here to reapply it into a fresh draft.
          </div>
        ) : (
          <div className="mt-6 grid gap-4 xl:grid-cols-2">
            {personalTemplates.map((page) => (
              <article key={page.id} className="rounded-[24px] border border-border bg-background p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">private reuse</p>
                    <h3 className="mt-2 text-xl font-semibold text-foreground">{page.title}</h3>
                  </div>
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    {new Date(page.updatedAt).toLocaleDateString()}
                  </span>
                </div>

                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                  Reapply this template into a new draft page. Marketplace listing is still a separate public publishing workflow.
                </p>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => void handleReapplyPersonalTemplate(page.id, page.title)}
                    disabled={activeTemplateId === page.id}
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                  >
                    {activeTemplateId === page.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Reapply template
                  </button>
                  <span className="inline-flex items-center rounded-xl border border-border bg-card px-3 py-2 text-xs text-muted-foreground">
                    Private only
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section
        ref={starterGalleryRef}
        className="rounded-[28px] border border-border bg-card p-8 shadow-sm"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl space-y-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              <LayoutTemplate className="h-3.5 w-3.5" />
              Pick One
            </span>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">Real vertical starters, not a fake marketplace.</h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Each starter below is backed by shared page-builder data with real section structure. Choose the vertical and starter page you want, then turn it into an AI checkpoint you can approve before editing.
              The next step is a calm customization pass for logo, colors, font mood, and business basics before any draft is generated.
              Once a starter becomes a normal draft page, saving it as a personal template uses the same private reuse ladder shown above.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{starterTemplates.length}</span> curated vertical starts
          </div>
        </div>

        {templateError && registryTemplates.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-amber-500/25 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-300">
            Template registry is unavailable right now, so the picker is using the shared package fallback instead of the mounted API.
          </div>
        ) : null}

        <div className="mt-8">
          <TemplateBrowser
            templates={starterTemplates}
            isLoading={templatesLoading && registryTemplates.length === 0}
            error={templateError && registryTemplates.length > 0 ? templateError : null}
            siteId={searchParams.get("siteId")}
          />
        </div>

        <div className="mt-8 rounded-[24px] border border-border bg-background p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Community templates
              </span>
              <h3 className="text-xl font-semibold text-foreground">Need public community packs instead of curated starters?</h3>
              <p className="text-sm leading-6 text-muted-foreground">
                Open the live marketplace to browse approved cross-tenant template packages, previews, and creator submissions. This stays separate from private template reuse so public imports are moderated before they appear.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                Browse community templates
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/marketplace/creator"
                className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
              >
                Publish your own pack
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function readStoredChoice(): PathId | null {
  const storedValue = localStorage.getItem(STORAGE_KEY);
  if (storedValue === "tell-me" || storedValue === "pick-one" || storedValue === "build-it") {
    return storedValue;
  }

  return null;
}
